/**
 * Loading Type. Used to represent data that is still loading, or has already been loaded.
 * @template T
 */
export type Loading<T, V = undefined> =
  | { status: 'not_started' }
  | { status: 'loading'; tempData?: V }
  | { status: 'loaded'; data: T }
  | { status: 'error'; error: Error }

export const notStarted = { status: 'not_started' } as const
export const loading = { status: 'loading' } as const
export const loadingWithData = <T, V>(tempData: V) =>
  ({ status: 'loading', tempData }) as Loading<T, V>
export const loaded = <T, V>(data: T) => ({ status: 'loaded', data }) as Loading<T, V>
export const error = <T, V>(error: Error) => ({ status: 'error', error }) as Loading<T, V>

/**
 * Checks if the value is still loading.
 * @template T
 * @param {Loading<T, V>} value - The value to check.
 * @returns {value is { status: 'loading'; tempData?: V }} - True if the value is still loading, false otherwise.
 */
export function isLoading<T, V>(
  value: Loading<T, V>
): value is { status: 'loading'; tempData?: V } {
  return value.status === 'loading'
}

/**
 * Checks if the value has already been loaded.
 * @template T
 * @param {Loading<T, V>} value - The value to check.
 * @returns {value is { status: 'loaded'; data: T }} - True if the value has already been loaded, false otherwise.
 */
export function isLoaded<T, V>(value: Loading<T, V>): value is { status: 'loaded'; data: T } {
  return value.status === 'loaded'
}

/**
 * Checks if the value has errored.
 * @template T
 * @param {Loading<T, V>} value - The value to check.
 * @returns {value is { status: 'error'; error: Error }} - True if the value has errored, false otherwise.
 */
export function isError<T, V>(value: Loading<T, V>): value is { status: 'error'; error: Error } {
  return value.status === 'error'
}

/**
 * Checks if the value has not started.
 * @template T
 * @param {Loading<T, V>} value - The value to check.
 * @returns {value is { status: 'not_started' }} - True if the value has not started, false otherwise.
 */
export function isNotStarted<T, V>(value: Loading<T, V>): value is { status: 'not_started' } {
  return value.status === 'not_started'
}
